import React, { useState } from "react"
import { Btn, FormInput, Loader, Modal } from "./";
import { useCore } from "../hooks";
import { OrderItem, PaymentItem, Voucher } from "depoto-core";
import { formatDate, formatPrice, OrderItemType, Schema, updateBill } from "../utils";
import { usePaginatedVouchers } from "../utils/hooks/usePaginatedVouchers";
import { CheckoutOrderItem } from "../store/core/reducer.ts";
import { toast } from "react-toastify";

export const ModalVoucherSearch: React.FC = () => {
  const { currentOrder, setCurrentOrder, orderVouchers, setOrderVouchers, checkouts, selectedCheckoutId, currency, company, vats } = useCore()
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [searchCode, setSearchCode] = useState<string>('')

  // TODO check validity for vouchers (date, maxUse, etc)

  const openModal = () => {
    setSearchCode('')
    setIsVisible(true)
  }

  const closeModal = () => {
    setSearchCode('')
    setIsVisible(false)
  }

  // single-purpose voucher (VAT paid on sale) is redeemed as negative item with the same VAT as on sale,
  // so the part paid by voucher is not taxed again; multi-purpose voucher is redeemed as payment
  const redeemAsOrderItem = (voucher: Partial<Voucher>): boolean => {
    const vat = vats?.find(v => v.default)
    if (!vat) {
      toast.error('Firma nemá výchozí sazbu DPH, poukaz nelze uplatnit.')
      return false
    }
    const orderItem: CheckoutOrderItem = new OrderItem({
      type: OrderItemType.voucher,
      name: `Uplatněný poukaz ${voucher.code}`,
      code: voucher.code,
      quantity: 1,
      vat,
    })
    orderItem.voucherId = voucher.id
    orderItem.voucherValue = Number(voucher.discountValue)
    const nextOrder = updateBill({ currentOrder, setCurrentOrder, orderItem })
    const redeemedItem = nextOrder.items.find(oi => oi.type === OrderItemType.voucher && oi.voucherId === voucher.id)
    if (redeemedItem && -redeemedItem.price < Number(voucher.discountValue)) {
      toast.warning(`Hodnota poukazu je vyšší než cena zboží se sazbou ${vat.percent} %, uplatní se jen ${formatPrice(-redeemedItem.price, currency?.id)}.`)
    }
    return true
  }

  const updateOrderVouchers = (voucher: Partial<Voucher>) => {
    if (orderVouchers?.find(pi => pi.code === voucher.code)) {
      toast.warning('Tento kupón je již v této objednávce použit!')
      return
    }
    if (voucher.discountType !== 'percentage' && !company?.multiPurposeVouchers) {
      if (redeemAsOrderItem(voucher)) {
        setOrderVouchers([...orderVouchers, voucher])
        closeModal()
      }
      return
    }
    const checkout = checkouts?.find(c => c.id === selectedCheckoutId)
    const nextVouchers = [...orderVouchers]
    nextVouchers.push(voucher)
    setOrderVouchers(nextVouchers)
    console.log(nextVouchers)
    if (voucher.discountType === 'percentage') {
      const nextPercentageSales = [...(currentOrder.percentageSales || [])]
      nextPercentageSales.push(voucher)
      currentOrder.percentageSales = nextPercentageSales
      updateBill({ currentOrder, setCurrentOrder })
    } else {
      const nextPaymentItems = currentOrder.paymentItems || []
      nextPaymentItems.push(new PaymentItem({
        payment: checkout.payments?.find(p => p.type.id === 'coupon'),
        amount: Number(voucher.discountValue),
        voucher,
      }))
      const nextOrder = { ...currentOrder }
      nextOrder.paymentItems = nextPaymentItems?.length ? [...nextPaymentItems] : []
      console.log({ ...nextOrder })
      setCurrentOrder(nextOrder)
    }
    closeModal()
  }

  const { vouchers, loading: areVouchersLoading } = usePaginatedVouchers({
    filters: {
      code: searchCode,
      isValid: true, // todo show only valid?
    },
    sort: 'code',
    direction: 'ASC',
    schema: Schema.voucher.detail,
  })

  return (
    <Modal
      modalOpenBtn={<Btn className="btn-primary" children={'Zvolit poukaz'} icon={'fa-badge-percent'} onClick={openModal} />}
      isVisible={isVisible}
      modalTitle={'Poukazy'}
      modalWidth={'lg'}
      onClose={closeModal}
      content={
        <div>
          <FormInput inputclassName="w-full" label={'Kód'} value={searchCode} onChange={e => setSearchCode(e)} />
          <div className="h-modal-content-scrollable scrollable-y mt-4">
            {vouchers?.length > 0 ? vouchers.map(v => (
              <div className="flex flex-col justify-between items-start border border-border-light p-2 mb-2 rounded-md" key={`modal-voucher-${v.code}-${v.id}`}>
                <div className="flex flex-row justify-between items-center w-full mb-3">
                  <div className="flex flex-col">
                    <span className="">Kód poukazu:</span>
                    <span className="font-bold">{v.code}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="">Hodnota poukazu:</span>
                    <span className="font-bold">{v.discountType === 'percentage' ? `${v.discountPercent}%` : formatPrice(v.discountValue, currency.id)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="">Platnost od:</span>
                    <span className="font-bold">{formatDate(new Date(v.validFrom), false)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="">Platnost do:</span>
                    <span className="font-bold">{formatDate(new Date(v.validTo), false)}</span>
                  </div>
                </div>
                <Btn className="btn-primary" icon={'fa-check'} children={'Uplatnit poukaz'} isDisabled={!v.isValid} onClick={() => updateOrderVouchers(v)} />
              </div>
            )) : null}
            {areVouchersLoading && <Loader />}
          </div>
        </div>
      }
      footerContent={
        <div className="flex flex-row justify-between items-center">
          <Btn className="btn-base-100" icon={'fa-times'} children={'Zrušit'} onClick={() => setIsVisible(false)} />
          {/*<Btn className="btn-primary" icon={'fa-check'} children={'Upravit poukazy'} onClick={updateOrderVouchers}/>*/}
        </div>
      }
    />
  )
}
